import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { TrendingUp, TrendingDown, Plus, Trash2, Brain, Shield, BarChart2, Target, Zap, RefreshCw, AlertTriangle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import MobileNav from '@/components/MobileNav';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/utils/i18n';
import { useTier } from '@/context/TierContext';

const FREEMIUM_LIMIT = 3;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function computeHealthScore(portfolio) {
  if (!portfolio || portfolio.length === 0) return null;

  const total = portfolio.reduce((s, a) => s + a.current_price * a.quantity, 0);
  if (total === 0) return null;

  const weights = portfolio.map(a => (a.current_price * a.quantity) / total);
  const maxWeight = Math.max(...weights);

  // Diversification: penalize if 1 asset > 50%
  const diversification = Math.max(0, Math.min(100, 100 - (maxWeight * 100 - 30) * 1.5));

  // Concentration risk: number of assets
  const assetCount = portfolio.length;
  const concentration = assetCount >= 5 ? 90 : assetCount >= 3 ? 70 : assetCount === 2 ? 50 : 30;

  // Balance: crypto vs stocks ratio
  const cryptoWeight = portfolio.filter(a => a.asset_type === 'crypto').reduce((s, a) => s + a.current_price * a.quantity, 0) / total;
  const balance = Math.max(0, 100 - Math.abs(cryptoWeight - 0.4) * 150);

  // Growth score (based on P&L)
  const pnl = portfolio.reduce((s, a) => s + a.profit_loss, 0);
  const cost = portfolio.reduce((s, a) => s + a.buy_price * a.quantity, 0);
  const growthScore = cost > 0 ? Math.min(100, Math.max(0, 50 + (pnl / cost) * 200)) : 50;

  const overall = Math.round((diversification * 0.3 + concentration * 0.25 + balance * 0.2 + growthScore * 0.25));

  // Insights
  const insights = [];
  if (maxWeight > 0.5) insights.push({ type: 'warning', text: `Overconcentrated: ${portfolio.find(a => (a.current_price * a.quantity) / total === maxWeight)?.symbol} represents ${(maxWeight * 100).toFixed(0)}% of your portfolio` });
  if (assetCount < 3) insights.push({ type: 'warning', text: 'Too few assets — consider diversifying across more positions' });
  if (cryptoWeight > 0.7) insights.push({ type: 'warning', text: 'Heavy crypto exposure — consider adding stocks for stability' });
  if (cryptoWeight < 0.1 && assetCount > 0) insights.push({ type: 'info', text: 'No crypto exposure — consider a small allocation for growth potential' });
  if (pnl < 0) insights.push({ type: 'info', text: 'Portfolio in loss — review your positions and consider stop-loss levels' });
  if (overall >= 75) insights.push({ type: 'success', text: 'Well-balanced portfolio with good diversification' });

  return { overall, diversification: Math.round(diversification), concentration, balance: Math.round(balance), growthScore: Math.round(growthScore), insights, cryptoWeight: Math.round(cryptoWeight * 100) };
}

function HealthScoreBar({ label, value, color = 'bg-[#ff6b35]' }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs text-gray-400">{label}</span>
        <span className="text-xs font-bold text-white">{value}/100</span>
      </div>
      <div className="w-full bg-gray-800 rounded-full h-1.5">
        <div className={`${color} h-1.5 rounded-full transition-all`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

export default function Portfolio() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const { hasAccess } = useTier();
  const [portfolio, setPortfolio] = useState([]);
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [loading, setLoading] = useState(false);
  const [healthLoading, setHealthLoading] = useState(false);
  const [showHealth, setShowHealth] = useState(false);
  const [assetForm, setAssetForm] = useState({ symbol: '', name: '', asset_type: 'crypto', quantity: '', buy_price: '' });

  useEffect(() => { fetchPortfolio(); }, []);

  const fetchPortfolio = async () => {
    try {
      const res = await axios.get(`${API}/portfolio`, { headers: { Authorization: `Bearer ${token}` } });
      setPortfolio(res.data);
    } catch (err) {
      toast.error('Failed to load portfolio');
    }
  };

  const handleAddAsset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/portfolio/asset`, { ...assetForm, quantity: parseFloat(assetForm.quantity), buy_price: parseFloat(assetForm.buy_price) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Asset added!');
      setShowAddAsset(false);
      setAssetForm({ symbol: '', name: '', asset_type: 'crypto', quantity: '', buy_price: '' });
      fetchPortfolio();
    } catch { toast.error('Failed to add asset'); }
    finally { setLoading(false); }
  };

  const handleDeleteAsset = async (id) => {
    try {
      await axios.delete(`${API}/portfolio/asset/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Asset removed');
      fetchPortfolio();
    } catch { toast.error('Failed to remove asset'); }
  };

  const isFreemium = !hasAccess('premium');
  const atFreemiumLimit = isFreemium && portfolio.length >= FREEMIUM_LIMIT;
  const totalValue = portfolio.reduce((s, a) => s + a.current_price * a.quantity, 0);
  const totalPL = portfolio.reduce((s, a) => s + a.profit_loss, 0);
  const totalCost = portfolio.reduce((s, a) => s + a.buy_price * a.quantity, 0);
  const totalPLPct = totalCost > 0 ? (totalPL / totalCost) * 100 : 0;
  const health = computeHealthScore(portfolio);

  const getHealthColor = (score) => {
    if (score >= 75) return 'text-green-400';
    if (score >= 50) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <MobileNav currentPage="portfolio" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-4xl font-black text-white">{t('myPortfolioPage')}</h1>
            <p className="text-gray-400 text-sm">{t('trackInvestments')}</p>
          </div>
          <Button onClick={() => setShowAddAsset(true)} className="bg-[#ff6b35] text-black font-bold text-sm" disabled={atFreemiumLimit}>
            <Plus className="w-4 h-4 mr-2" />
            {t('addAsset')}
          </Button>
        </div>

        {/* Freemium limit banner */}
        {atFreemiumLimit && (
          <div className="glass rounded-2xl p-4 mb-5 border border-orange-500/30 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-orange-400" />
              <p className="text-sm text-orange-300">
                {language === 'fr' ? `Limite gratuite : ${FREEMIUM_LIMIT} actifs max. Passez à Premium pour un portfolio illimité.` : language === 'es' ? `Límite gratuito: ${FREEMIUM_LIMIT} activos máx. Actualiza a Premium para un portfolio ilimitado.` : `Free limit: ${FREEMIUM_LIMIT} assets max. Upgrade to Premium for unlimited portfolio.`}
              </p>
            </div>
            <Button onClick={() => window.location.href = '/pricing'} size="sm" className="bg-[#ff6b35] text-black font-bold text-xs">
              {language === 'fr' ? 'Voir les plans' : language === 'es' ? 'Ver planes' : 'Upgrade'} →
            </Button>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="glass rounded-2xl p-4">
            <div className="text-xs text-gray-400 mb-1">{t('totalValue')}</div>
            <div className="text-xl sm:text-2xl font-black">${totalValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
          </div>
          <div className="glass rounded-2xl p-4">
            <div className="text-xs text-gray-400 mb-1">{t('totalPL')}</div>
            <div className={`text-xl sm:text-2xl font-black ${totalPL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {totalPL >= 0 ? '+' : '-'}${Math.abs(totalPL).toLocaleString('en-US', { maximumFractionDigits: 0 })}
            </div>
          </div>
          <div className="glass rounded-2xl p-4">
            <div className="text-xs text-gray-400 mb-1">{t('performance')}</div>
            <div className={`text-xl sm:text-2xl font-black ${totalPLPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {totalPLPct >= 0 ? '+' : ''}{totalPLPct.toFixed(1)}%
            </div>
          </div>
          <div className="glass rounded-2xl p-4 cursor-pointer hover:border-[#ff6b35]/30 border border-transparent transition-all" onClick={() => health && setShowHealth(!showHealth)}>
            <div className="text-xs text-gray-400 mb-1 flex items-center gap-1">
              <Shield className="w-3 h-3" /> {t('aiHealth')}
            </div>
            <div className={`text-xl sm:text-2xl font-black ${health ? getHealthColor(health.overall) : 'text-gray-600'}`}>
              {health ? `${health.overall}/100` : 'N/A'}
            </div>
          </div>
        </div>

        {/* AI Portfolio Health Score */}
        {health && (showHealth || portfolio.length > 0) && (
          <div className="glass rounded-2xl p-5 mb-5 border border-[#ff6b35]/10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-[#ff6b35]/10 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-[#ff6b35]" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">{t('aiPortfolioHealth')}</h3>
                  <p className="text-xs text-gray-500">{t('personalizedAnalysis')}</p>
                </div>
              </div>
              <div className={`text-3xl font-black ${getHealthColor(health.overall)}`}>{health.overall}<span className="text-sm text-gray-500">/100</span></div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div className="space-y-3">
                <HealthScoreBar label={t('diversification')} value={health.diversification} color="bg-blue-400" />
                <HealthScoreBar label={t('concentrationRisk')} value={health.concentration} color="bg-purple-400" />
              </div>
              <div className="space-y-3">
                <HealthScoreBar label={t('assetBalance')} value={health.balance} color="bg-green-400" />
                <HealthScoreBar label={t('growthScore')} value={health.growthScore} color="bg-[#ff6b35]" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4 text-center text-xs">
              <div className="bg-[#0a0a0a]/60 rounded-lg p-2">
                <div className="text-gray-500 mb-0.5">{t('assetsCount')}</div>
                <div className="font-bold text-white">{portfolio.length}</div>
              </div>
              <div className="bg-[#0a0a0a]/60 rounded-lg p-2">
                <div className="text-gray-500 mb-0.5">{t('cryptoPct')}</div>
                <div className="font-bold text-orange-400">{health.cryptoWeight}%</div>
              </div>
              <div className="bg-[#0a0a0a]/60 rounded-lg p-2">
                <div className="text-gray-500 mb-0.5">{t('stocksPct')}</div>
                <div className="font-bold text-blue-400">{100 - health.cryptoWeight}%</div>
              </div>
            </div>

            {health.insights.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-400 mb-2">{t('aiInsights')}</p>
                {health.insights.map((ins, i) => (
                  <div key={i} className={`flex items-start gap-2 p-2.5 rounded-lg text-xs ${
                    ins.type === 'warning' ? 'bg-yellow-400/10 border border-yellow-400/20 text-yellow-300'
                    : ins.type === 'success' ? 'bg-green-400/10 border border-green-400/20 text-green-300'
                    : 'bg-blue-400/10 border border-blue-400/20 text-blue-300'
                  }`}>
                    {ins.type === 'warning' ? <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    : ins.type === 'success' ? <Shield className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    : <Zap className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />}
                    {ins.text}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Assets List */}
        <div className="glass rounded-2xl p-5">
          <h2 className="font-bold mb-4 flex items-center gap-2 text-sm">
            <BarChart2 className="w-4 h-4 text-[#ff6b35]" />
            {t('myAssets')} ({portfolio.length})
          </h2>

          {portfolio.length > 0 ? (
            <div className="space-y-2">
              {portfolio.map((asset, idx) => (
                <div key={asset.id} data-testid={`asset-row-${idx}`} className="flex items-center gap-3 p-3 sm:p-4 bg-[#0a0a0a]/50 rounded-xl hover:bg-[#0a0a0a] transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-[#ff6b35]/10 flex items-center justify-center flex-shrink-0">
                    <span className="font-black text-[#ff6b35] text-xs">{asset.symbol.substring(0, 2)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm">{asset.symbol} <span className="text-xs text-gray-500 font-normal">• {asset.asset_type}</span></div>
                    <div className="text-xs text-gray-500 truncate">{asset.name} • {asset.quantity} units</div>
                  </div>
                  <div className="hidden sm:block text-right">
                    <div className="text-xs text-gray-500">Buy Price</div>
                    <div className="text-sm font-semibold">${asset.buy_price.toFixed(2)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Current</div>
                    <div className="text-sm font-bold">${asset.current_price.toFixed(2)}</div>
                  </div>
                  <div className="text-right min-w-[70px]">
                    <div className={`text-sm font-black ${asset.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {asset.profit_loss >= 0 ? '+' : ''}{asset.profit_loss_percentage.toFixed(1)}%
                    </div>
                    <div className={`text-xs ${asset.profit_loss >= 0 ? 'text-green-400/70' : 'text-red-400/70'}`}>
                      {asset.profit_loss >= 0 ? '+' : ''}${Math.abs(asset.profit_loss).toFixed(0)}
                    </div>
                  </div>
                  <Button onClick={() => handleDeleteAsset(asset.id)} variant="ghost" size="icon" className="text-red-400/50 hover:text-red-400 hover:bg-red-400/10 flex-shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <TrendingUp className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500 text-sm mb-4">{t('portfolioEmpty')}</p>
              <Button onClick={() => setShowAddAsset(true)} size="sm" className="bg-[#ff6b35] text-black font-bold">
                <Plus className="w-3 h-3 mr-1" /> {t('addFirstAsset')}
              </Button>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-600 mt-4 text-center">{t('aiHealthDisclaimer')}</p>
      </div>

      {/* Add Asset Dialog */}
      <Dialog open={showAddAsset} onOpenChange={setShowAddAsset}>
        <DialogContent className="bg-[#111] border-[#ff6b35]/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-black">{t('addAsset')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddAsset} className="space-y-4">
            <div>
              <label className="text-xs text-gray-400 mb-1.5 block">{t('assetType')}</label>
              <Select value={assetForm.asset_type} onValueChange={(v) => setAssetForm({ ...assetForm, asset_type: v })}>
                <SelectTrigger className="bg-black border-gray-700 text-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-gray-700 text-white">
                  <SelectItem value="crypto">Crypto</SelectItem>
                  <SelectItem value="stock">{language === 'fr' ? 'Action' : language === 'es' ? 'Acción' : 'Stock'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {[
              { key: 'symbol', label: t('symbol'), placeholder: 'BTC, ETH, AAPL...' },
              { key: 'name', label: t('name'), placeholder: 'Bitcoin, Apple...' },
              { key: 'quantity', label: t('quantity'), placeholder: '0.5', type: 'number' },
              { key: 'buy_price', label: t('buyPrice') + ' ($)', placeholder: '50000', type: 'number' }
            ].map(f => (
              <div key={f.key}>
                <label className="text-xs text-gray-400 mb-1.5 block">{f.label}</label>
                <Input type={f.type || 'text'} step={f.type === 'number' ? '0.00000001' : undefined} placeholder={f.placeholder}
                  value={assetForm[f.key]} onChange={(e) => setAssetForm({ ...assetForm, [f.key]: f.key === 'symbol' ? e.target.value.toUpperCase() : e.target.value })}
                  className="bg-black border-gray-700 text-white text-sm" required />
              </div>
            ))}
            <Button type="submit" disabled={loading} className="w-full bg-[#ff6b35] text-black font-bold">
              {loading ? t('adding') : t('addAsset')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
